import type { Stop } from '@/@types/gtfs-ressource';
import { MapLabelPriority, type MapShape, type MapStop } from '@/@types/map';
import { StopType } from '@/@types/trip-updates';
import type { WalkLeg } from '@/libs/journey/journeys';
import type { Place } from '@/libs/journey/places';

export type JourneyEnd = 'origin' | 'destination';

/** An address the journey starts from or ends at: no stop, only its marker. */
const JOURNEY_POINT_ID = { origin: 'journey-origin', destination: 'journey-destination' } as const;

/** A chosen end of the itinerary as a named marker: the arrival is a pin. The passenger's position has its own dot. */
export function placeMarker(
  place: Place | undefined,
  end: JourneyEnd,
  stops: { [stopId: string]: Stop },
): MapStop | undefined {
  const isArrival = end === 'destination';
  const marker = {
    terminus: isArrival,
    zoomLevel: { stops: true, stations: true, terminus: true },
    children: [],
    type: StopType.REGULAR,
    labelPriority: isArrival ? MapLabelPriority.ARRIVAL : MapLabelPriority.DEPARTURE,
  };
  if (place?.kind === 'point') {
    return {
      ...marker,
      id: JOURNEY_POINT_ID[end],
      name: place.label ?? '',
      position: { lng: place.lon, lat: place.lat },
      parentStation: '',
    };
  }
  const stop = place?.kind === 'stop' ? stops[place.stopId] : undefined;
  if (!stop) return undefined;
  return {
    ...marker,
    id: stop.stop_id,
    name: stop.stop_name,
    position: { lng: stop.stop_lon, lat: stop.stop_lat },
    parentStation: stop.parent_station,
  };
}

export function endMarkers(
  origin: Place | undefined,
  destination: Place | undefined,
  stops: { [stopId: string]: Stop },
): { [key: string]: MapStop } {
  return [placeMarker(origin, 'origin', stops), placeMarker(destination, 'destination', stops)]
    .filter((end): end is MapStop => !!end)
    .reduce((byId, end) => ({ ...byId, [end.id]: end }), {});
}

/** Along the streets once measured, a straight line while estimated. */
export function walkShape(leg: WalkLeg): MapShape {
  return {
    geometry: {
      type: 'LineString',
      coordinates: leg.path ?? [
        [leg.from.lon, leg.from.lat],
        [leg.to.lon, leg.to.lat],
      ],
    },
    properties: { routeId: undefined, color: '' },
  };
}
